// biome-ignore lint/style/useImportType: <explanation>

import { Sprite, Texture, Application, Container } from "pixi.js";

import { IBackgroundType } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Theme.interface";

import type { ISceneDB } from "../../../../interfaces/Scenes";

interface IRenderOptions {
  width: number;

  height: number;
}

class BackgroundImageRenderer {
  private BackgroundSprite?: Sprite;

  private Root: Container;

  private Options: IRenderOptions;

  constructor(_app: Application, options: IRenderOptions, root: Container) {
    this.Root = root;

    this.Options = options;
  }

  render(scene: ISceneDB): void {
    const model = scene.model;

    if (
      model.theme?.background?.type !== IBackgroundType.image ||
      !model.theme.background.image
    )
      return;

    if (this.BackgroundSprite) {
      if (!this.BackgroundSprite.destroyed && !this.BackgroundSprite.parent) {
        // Update bounds in case they changed

        const bounds = model.theme?.background.bounds;

        const spriteWidth = (bounds.width / 100) * this.Options.width;

        const spriteHeight = (bounds.height / 100) * this.Options.height;

        const spriteX = (bounds.left / 100) * this.Options.width;

        const spriteY = (bounds.top / 100) * this.Options.height;

        this.BackgroundSprite.width = spriteWidth;

        this.BackgroundSprite.height = spriteHeight;

        this.BackgroundSprite.x = spriteX;

        this.BackgroundSprite.y = spriteY;

        this.Root.addChild(this.BackgroundSprite);

        this.Root.sortableChildren = true;
      }

      return;
    }

    const existingBackground = this.Root.children.find(
      (child) => child.label === "background-image",
    );

    if (existingBackground) {
      this.Root.removeChild(existingBackground);
    }

    const objectUrl = URL.createObjectURL(model.theme.background.image);

    const img = new Image();

    img.crossOrigin = "anonymous";

    img.onload = () => {
      if (
        !model.theme?.background ||
        model.theme?.background?.type !== IBackgroundType.image
      )
        return;

      const backgroundTexture = Texture.from(img);

      const backgroundSprite = new Sprite(backgroundTexture);

      const spriteWidth = this.Options.width;

      const spriteHeight = this.Options.height;

      const spriteX = 0;

      const spriteY = 0;

      backgroundSprite.width = spriteWidth;

      backgroundSprite.height = spriteHeight;

      backgroundSprite.x = spriteX;

      backgroundSprite.y = spriteY;

      backgroundSprite.zIndex = -1000;

      backgroundSprite.label = "background-image";

      backgroundSprite.alpha = 1;

      this.Root.addChild(backgroundSprite);

      this.Root.sortableChildren = true;

      // Static image — cache as texture so PixiJS skips re-rendering each tick

      backgroundSprite.cacheAsTexture(true);

      // Cache the background sprite

      this.BackgroundSprite = backgroundSprite;
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  }

  clear(): void {
    if (this.BackgroundSprite && !this.BackgroundSprite.destroyed) {
      this.BackgroundSprite.destroy({ children: true, texture: false });

      this.BackgroundSprite = undefined;
    }
  }

  invalidateCache(): void {
    this.BackgroundSprite = undefined;
  }

  updateOptions(options: IRenderOptions): void {
    this.Options = options;
  }
}

export default BackgroundImageRenderer;
